import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Loader2 } from "lucide-react";
import { createOrganization } from "@/api/organizations";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useOrganizationStore } from "@/store/organization";
import { applyActivatedOrganization } from "../lib/apply-activated-organization";

/**
 * Offers an explicit retry of the server-owned organization signup saga after
 * a partial provider failure. The browser never provisions credits directly.
 *
 * @returns Recovery instructions, error feedback, and an explicit retry action.
 */
export const OrganizationSignupRecoveryStep = () => {
  const organization = useOrganizationStore((s) => s.organization);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    retry: false,
    mutationFn: () => {
      if (!organization) {
        throw new Error("Organization is unavailable for signup recovery");
      }
      return createOrganization(organization.name, {
        selfServeBillingEnabled: true,
        resumeOrganizationId: organization.id,
      });
    },
    onSuccess: (activated) =>
      applyActivatedOrganization(queryClient, activated.id, activated),
  });

  return (
    <>
      <div>
        <h2 className="text-3xl font-bold tracking-tight">
          Preparing your API credits
        </h2>
        <p className="mt-3 text-muted-foreground">
          Organization setup did not finish. Retry the server-owned signup
          operation to complete your credit balance.
        </p>
      </div>
      {mutation.isError && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            Organization setup failed again. Please try again.
          </AlertDescription>
        </Alert>
      )}
      <Button
        className="w-full"
        disabled={!organization || mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        {mutation.isPending && (
          <Loader2 className="mr-2 w-4 h-4 animate-spin" />
        )}
        Retry Organization Setup
      </Button>
    </>
  );
};
